import type { Step3Data } from '../../types/registration'
import GoogleConnectButton from './GoogleConnectButton'

interface Step3LocationProps {
  data: Step3Data
  onChange: (data: Step3Data) => void
  errors: Partial<Record<keyof Step3Data, string>>
  onSubmit: () => void
  onBack: () => void
  isSubmitting: boolean
  submitError?: string
  plan: string
}

export default function Step3Location({
  data,
  onChange,
  errors,
  onSubmit,
  onBack,
  isSubmitting,
  submitError,
  plan,
}: Step3LocationProps) {
  const handleChange =
    (field: keyof Step3Data) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...data, [field]: e.target.value })
    }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit()
  }

  const canConnectJudgeMe = plan === 'pro' || plan === 'agency'

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      {/* Location Name */}
      <div>
        <div className="flex items-center gap-1.5 mb-1">
          <label htmlFor="location_name" className="text-sm font-medium text-rd-muted">
            Location Name <span aria-hidden="true" className="text-red-400">*</span>
          </label>
          <span className="relative group" aria-hidden="true">
            <svg className="w-3.5 h-3.5 text-rd-dim cursor-help" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 rounded-lg bg-rd-surface2 border border-white/10 px-3 py-2 text-xs text-rd-muted shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10">
              A label for your own reference inside ReputeDesk — only your team sees this (e.g. "Downtown Branch", "Novi Location"). The full address below is what identifies your Google Maps listing.
            </span>
          </span>
        </div>
        <input
          id="location_name"
          type="text"
          value={data.location_name}
          onChange={handleChange('location_name')}
          aria-required="true"
          aria-describedby={errors.location_name ? 'location_name-error' : undefined}
          aria-invalid={!!errors.location_name}
          className={`reg-input ${errors.location_name ? 'border-red-500/60' : ''}`}
          placeholder="Jane's Bakery - Downtown"
        />
        {errors.location_name && (
          <p id="location_name-error" role="alert" className="mt-1 text-sm text-red-400">
            {errors.location_name}
          </p>
        )}
      </div>

      {/* Location Address */}
      <div>
        <label htmlFor="location_address" className="block text-sm font-medium text-rd-muted mb-1">
          Street Address <span aria-hidden="true" className="text-red-400">*</span>
        </label>
        <input
          id="location_address"
          type="text"
          autoComplete="street-address"
          value={data.location_address}
          onChange={handleChange('location_address')}
          aria-required="true"
          aria-describedby={errors.location_address ? 'location_address-error' : undefined}
          aria-invalid={!!errors.location_address}
          className={`reg-input ${errors.location_address ? 'border-red-500/60' : ''}`}
          placeholder="123 Main St, Austin TX"
        />
        {errors.location_address && (
          <p id="location_address-error" role="alert" className="mt-1 text-sm text-red-400">
            {errors.location_address}
          </p>
        )}
      </div>

      {/* Notification Email */}
      <div>
        <label htmlFor="notification_email" className="block text-sm font-medium text-rd-muted mb-1">
          Notification Email <span aria-hidden="true" className="text-red-400">*</span>
        </label>
        <input
          id="notification_email"
          type="email"
          autoComplete="email"
          value={data.notification_email}
          onChange={handleChange('notification_email')}
          aria-required="true"
          aria-describedby="notification_email-hint notification_email-error"
          aria-invalid={!!errors.notification_email}
          className={`reg-input ${errors.notification_email ? 'border-red-500/60' : ''}`}
          placeholder="jane@example.com"
        />
        <p id="notification_email-hint" className="mt-1 text-xs text-rd-dim">
          Approval emails for review responses will be sent to this address.
        </p>
        {errors.notification_email && (
          <p id="notification_email-error" role="alert" className="mt-1 text-sm text-rd-red">
            {errors.notification_email}
          </p>
        )}
      </div>

      {/* Google Connect */}
      <div>
        <p className="block text-sm font-medium text-rd-muted mb-1">
          Google Business Profile
        </p>
        <p className="text-xs text-rd-dim mb-3">
          Connect your Google Business Profile so ReputeDesk can fetch and respond to your reviews.
        </p>
        <GoogleConnectButton
          isConnected={!!data.google_credentials}
          onConnect={(encryptedCredentials) =>
            onChange({ ...data, google_credentials: encryptedCredentials })
          }
        />
      </div>

      {/* Judge.me */}
      <div>
        <p className="block text-sm font-medium text-rd-muted mb-1">
          Judge.me <span className="text-rd-dim font-normal">(optional)</span>
        </p>
        {canConnectJudgeMe ? (
          <div className="space-y-2">
            <p className="text-xs text-rd-dim">
              Also manage Shopify product reviews from Judge.me alongside Google.
            </p>
            <input
              id="judge_me_api_token"
              type="password"
              value={data.judge_me_api_token ?? ''}
              onChange={handleChange('judge_me_api_token')}
              placeholder="API token"
              autoComplete="off"
              className="reg-input"
            />
            <input
              id="judge_me_shop_domain"
              type="text"
              value={data.judge_me_shop_domain ?? ''}
              onChange={handleChange('judge_me_shop_domain')}
              placeholder="my-store.myshopify.com"
              autoComplete="off"
              className="reg-input"
            />
          </div>
        ) : (
          <p className="text-xs text-rd-dim">
            Available on <span className="text-rd-muted font-medium">Pro</span> and{' '}
            <span className="text-rd-muted font-medium">Agency</span> plans.
          </p>
        )}
      </div>

      {/* Submit Error */}
      {submitError && (
        <div
          role="alert"
          className="rounded-lg bg-[rgba(239,68,68,0.08)] border border-red-500/30 px-4 py-3 text-sm text-red-400"
        >
          {submitError}
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onBack}
          disabled={isSubmitting}
          className="reg-btn-secondary flex-1"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          aria-busy={isSubmitting}
          className="reg-btn-primary flex-1"
        >
          {isSubmitting ? (
            <>
              <svg
                aria-hidden="true"
                className="h-4 w-4 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              Submitting…
            </>
          ) : (
            'Complete Registration'
          )}
        </button>
      </div>
    </form>
  )
}
